import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from "@angular/core";
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from "rxjs";
import { AuthService, SKIP_AUTH_RETRY } from 'src/app/services/core/auth.service';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';

export { SKIP_AUTH_RETRY };

// APIs tierces (weatherapi.com, Google Maps, ...) dosn't need our auth headers, so we only attach them to requests to our own APIs.
const APP_API_ORIGINS = [
  environment.AUTH_API,
  environment.API_URL,
  environment.DEV_API_URL,
  environment.FLOW_URL,
  environment.FLOW_REPLAY_URL,
].filter(Boolean);

function isAppApiRequest(url: string): boolean {
  return APP_API_ORIGINS.some((origin) => url.startsWith(origin));
}

// In Angular 18 interceptors must now respect a new functional signature if using withInterceptors
// Based on functions and no longer classes
export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  // Ignore JSONP requests, as they don't support custom headers and are not used for our API calls.
  if (req.method === 'JSONP') {
    return next(req);
  }

  // Not attaching auth headers to requests to third-party APIs (like weatherapi.com, Google Maps, ...) because they don't need our auth headers.
  if (!isAppApiRequest(req.url)) {
    return next(req);
  }

  // Allows to cleanly inject the AuthService service without needing to build it manually.
  const localStorage = inject(LocalStorageService);
  const token = localStorage.getItem<string>("authToken");
  const email = localStorage.getItem<string>("email");
  const project = localStorage.getItem<string>("project");
  const accessKey = localStorage.getItem<string>("accessKey");
  const db = sessionStorage.getItem("db") || localStorage.getItem<string>("db");
  // A caller can force a specific tenant database (e.g. admin screens that operate on a
  // project other than the one currently active in the session) by setting this header
  // on the outgoing request before it reaches this interceptor.
  const dbOverride = req.headers.get('X-Project-Db');

  const auth = inject(AuthService);
  const router = inject(Router);
  const authToken = auth.getAccessToken();

  if (authToken) {
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${authToken}`,
        'X-Project-Db': dbOverride ?? db ?? ""
      },
      withCredentials: true,
    });

    return next(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        const isAuthRoute = req.url.includes('/auth/');
        const alreadyRetried = req.context.get(SKIP_AUTH_RETRY);

        if (err.status !== 401 || isAuthRoute || alreadyRetried) {
          return throwError(() => err);
        }

        return from(auth.refresh()).pipe(
          switchMap((ok) => {
            if (!ok) {
              // refresh() a déjà appelé clear() ; on renvoie l'utilisateur vers le login.
              auth.requireLogin(router.url);
              return throwError(() => err);
            }
            const newToken = auth.getAccessToken();
            const retried = req.clone({
              setHeaders: newToken ? {
                Authorization: `Bearer ${newToken}`,
                'X-Project-Db': dbOverride ?? db ?? ""
              } : {},
              withCredentials: true,
              context: req.context.set(SKIP_AUTH_RETRY, true),
            });
            return next(retried);
          }),
        );
      }),
    );
  } else if (token && email) {
    const cloneRequest = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-Access-Token': token,
        'X-Key': email,
        'X-Project-Db': dbOverride ?? db ?? ""
      },
    });

    return next(cloneRequest).pipe(
      catchError((error) => {
        if (error.status === 401) {
          // authService.logout();
        }

        return throwError(() => error);
      })
    );
  } else if (project && accessKey) {
    const cloneRequest = req.clone({
      setHeaders: {
        'X-Access-Key': accessKey,
        'X-Project-Id': project,
      },
    });

    return next(cloneRequest).pipe(
      catchError((error) => {
        if (error.status === 401) {
          // authService.logout();
        }

        return throwError(() => error);
      })
    );
  }
  return next(req);
}